import dayjs from "dayjs";
import type { InsolvencyType } from "@/types/entities/insolvencies";

export interface BadgeConfig {
  badgeLabel: string;
  badgeVariant: "danger" | "success" | "warning" | "neutral";
}

export const getInsolvencyableName = (insolvency: InsolvencyType): string => {
  if (!insolvency.insolvencyable?.data) return "N/A";
  const { data } = insolvency.insolvencyable;
  if (insolvency.insolvencyable.type === "applicant") {
    return (
      `${data.first_name || ""} ${data.last_name || ""}`.trim() || "Sin nombre"
    );
  }

  return data.commercial_name || data.name || "Sin nombre";
};

export const getInsolvencyableId = (insolvency: InsolvencyType): string => {
  if (!insolvency.insolvencyable?.data) return "N/A";

  const { data } = insolvency.insolvencyable;

  if (insolvency.insolvencyable.type === "applicant") {
    return data.email || "N/A";
  }

  return data.code || "N/A";
};

export const getInsolvencyType = (
  insolvency: InsolvencyType,
): "personal" | "property" => {
  return insolvency.insolvencyable?.type === "applicant"
    ? "personal"
    : "property";
};

export const parseAmount = (amount: string | number): number => {
  if (typeof amount === "number") return amount;
  return parseFloat(amount) || 0;
};

export const getPersonInitial = (name: string) => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return "?";
  }

  return trimmedName.charAt(0).toUpperCase();
};

export const getDaysSinceCreated = (createdAt: string): number => {
  const today = dayjs();
  const createdDate = dayjs(createdAt);
  return today.diff(createdDate, "day");
};

export const getStatusConfig = (infraction: InsolvencyType): BadgeConfig => {
  const daysSinceCreated = getDaysSinceCreated(infraction.created_at);
  const totalDeadlineDays = convertUnitToDays(
    infraction.payment_deadline_value,
    infraction.payment_deadline_unit,
  );

  if (infraction.status === "ACTIVE") {
    // Check if payment deadline has passed
    if (totalDeadlineDays <= daysSinceCreated) {
      // Payment overdue - show danger (insolvencia)
      return {
        badgeLabel: "Insolvencia",
        badgeVariant: "danger",
      };
    } else {
      // Payment still has time - show warning
      return {
        badgeLabel: "Pendiente",
        badgeVariant: "warning",
      };
    }
  }

  // Both cancelled show as "Anulado"
  if (infraction.status === "CANCELLED") {
    return {
      badgeLabel: "Anulado",
      badgeVariant: "neutral",
    };
  }

  if (infraction.status === "SOLVENT") {
    return {
      badgeLabel: "Solvente",
      badgeVariant: "success",
    };
  }

  if (infraction.status === "APPEAL_SUBMITTED") {
    return {
      badgeLabel: "Apelación enviada",
      badgeVariant: "warning",
    };
  }

  if (infraction.status === "APPEAL_REJECTED") {
    return {
      badgeLabel: "Apelación rechazada",
      badgeVariant: "danger",
    };
  }

  // Fallback
  return {
    badgeLabel: "Desconocido",
    badgeVariant: "neutral",
  };
};

/**
 *  Gets the display text for a given time unit in Spanish.
 * @param unit
 * @returns
 */
export const getUnitText = (unit: string): string => {
  switch (unit?.toUpperCase()) {
    case "HOURS":
      return "horas";
    case "DAYS":
      return "días";
    case "WEEKS":
      return "semanas";
    case "MONTHS":
      return "meses";
    default:
      return "";
  }
};

/**
 *  Converts the given value and unit to days.
 * @param value
 * @param unit
 * @returns
 */
export const convertUnitToDays = (value: number, unit: string): number => {
  switch (unit?.toUpperCase()) {
    case "HOURS":
      return value / 24;
    case "DAYS":
      return value;
    case "WEEKS":
      return value * 7;
    case "MONTHS":
      return value * 30;
    default:
      return value;
  }
};
